/**
 * Fix Orphaned Documents
 * This script finds documents that have customer data but no customer_id relationship
 * and attempts to link them to the correct customer in the database
 */

import { directSupabase } from '../lib/supabaseClient';

export async function fixOrphanedDocuments() {
  console.log('🔄 Starting orphaned document fix...');
  
  try {
    // Step 1: Find documents with customer names but no customer_id
    console.log('📊 Finding orphaned documents...');
    const { data: orphanedDocs, error: docError } = await directSupabase
      .from('documents')
      .select('id, title, customer_first_name, customer_last_name, customer_title, customer_id')
      .is('customer_id', null)
      .not('customer_first_name', 'is', null)
      .not('customer_last_name', 'is', null);
    
    if (docError) {
      throw new Error(`Failed to fetch orphaned documents: ${docError.message}`);
    }
    
    console.log(`Found ${orphanedDocs?.length || 0} orphaned documents with customer data`);
    
    if (!orphanedDocs || orphanedDocs.length === 0) {
      console.log('✅ No orphaned documents found!');
      return {
        success: true,
        totalDocuments: 0,
        fixed: 0,
        results: []
      };
    }
    
    // Step 2: For each orphaned document, try to find matching customer
    let fixedCount = 0;
    const results = [];
    
    for (const doc of orphanedDocs) {
      try {
        const docName = `${doc.customer_first_name} ${doc.customer_last_name}`.trim();
        console.log(`🔍 Processing "${doc.title}" - Customer: ${docName}`);
        
        // Try to find matching customer by name
        const { data: customers, error: customerError } = await directSupabase
          .from('customers')
          .select('id, name, title')
          .ilike('name', `%${docName}%`);
        
        if (customerError) {
          console.warn(`⚠️ Error searching for customer "${docName}":`, customerError);
          results.push({
            documentId: doc.id,
            documentTitle: doc.title,
            customerName: docName,
            status: 'search_error',
            error: customerError.message
          });
          continue;
        }
        
        if (!customers || customers.length === 0) {
          console.log(`⚠️ No matching customer found for "${docName}"`);
          results.push({
            documentId: doc.id,
            documentTitle: doc.title,
            customerName: docName,
            status: 'no_match'
          });
          continue;
        }
        
        // If multiple matches, prefer exact match
        let matchedCustomer = customers.find(c => c.name.toLowerCase() === docName.toLowerCase());
        if (!matchedCustomer) {
          matchedCustomer = customers[0]; // Take first partial match
          console.log(`⚠️ Using partial match for "${docName}" → "${matchedCustomer.name}"`);
        } else {
          console.log(`✅ Exact match found for "${docName}" → "${matchedCustomer.name}"`);
        }
        
        // Update document with customer_id
        console.log(`🔗 Linking "${doc.title}" to customer: ${matchedCustomer.name} (${matchedCustomer.id})`);
        
        const { error: updateError } = await directSupabase
          .from('documents')
          .update({ 
            customer_id: matchedCustomer.id,
            // Also update customer title if customer has one and document doesn't
            customer_title: doc.customer_title || matchedCustomer.title || null
          })
          .eq('id', doc.id);
        
        if (updateError) {
          console.error(`❌ Failed to update document "${doc.title}":`, updateError);
          results.push({
            documentId: doc.id,
            documentTitle: doc.title,
            customerName: docName,
            matchedCustomer: matchedCustomer.name,
            status: 'update_failed',
            error: updateError.message
          });
        } else {
          fixedCount++;
          console.log(`✅ Fixed "${doc.title}" → ${matchedCustomer.name}`);
          results.push({
            documentId: doc.id,
            documentTitle: doc.title,
            customerName: docName,
            matchedCustomer: matchedCustomer.name,
            customerId: matchedCustomer.id,
            status: 'success'
          });
        }
        
      } catch (error) {
        console.error(`❌ Error processing document "${doc.title}":`, error);
        results.push({
          documentId: doc.id,
          documentTitle: doc.title,
          customerName: `${doc.customer_first_name} ${doc.customer_last_name}`.trim(),
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
    
    // Step 3: Summary
    console.log('✅ Orphaned document fix completed!');
    console.log(`📊 Summary:`);
    console.log(`   - Total orphaned documents: ${orphanedDocs.length}`);
    console.log(`   - Successfully fixed: ${fixedCount}`);
    console.log(`   - No match found: ${results.filter(r => r.status === 'no_match').length}`);
    console.log(`   - Update failed: ${results.filter(r => r.status === 'update_failed').length}`);
    console.log(`   - Errors: ${results.filter(r => r.status === 'error').length}`);
    
    // Show successful fixes
    const successful = results.filter(r => r.status === 'success');
    if (successful.length > 0) {
      console.log('✅ Successfully fixed documents:');
      console.table(successful);
    }
    
    // Show no matches
    const noMatches = results.filter(r => r.status === 'no_match');
    if (noMatches.length > 0) {
      console.log('⚠️ Documents with no customer matches:');
      console.table(noMatches);
    }
    
    // Show failures
    const failed = results.filter(r => r.status === 'update_failed');
    if (failed.length > 0) {
      console.log('❌ Failed updates:');
      console.table(failed);
    }
    
    return {
      success: true,
      totalDocuments: orphanedDocs.length,
      fixed: fixedCount,
      noMatch: noMatches.length,
      failed: failed.length,
      results
    };
    
  } catch (error) {
    console.error('❌ Orphaned document fix failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).fixOrphans = fixOrphanedDocuments;
















